// =====================================================================
// iSentinel360 v0.4.1 — OfflineIndicator (banner only, v9.5)
//
// === v9.5 fix: NOT a fullscreen overlay ===
//
// v9.4 was a fullscreen `fixed inset-0` overlay that covered
// the entire viewport, including the page content. The user
// reported the GuardDashboard and login page were "overlapping"
// with the offline indicator — because they WERE overlapping.
// A fullscreen overlay always overlaps whatever's underneath.
//
// v9.5 is just a small banner at the top of the page (below the
// Header). It does NOT cover the page content. The page can
// still render normally — the user just sees a red bar at the
// top saying "No internet connection - some features won't work".
//
// === The big red shield is now per-page (OfflineShield) ===
//
// The big red shield that matches the GuardDashboard's alert
// shield design (line 1456) is no longer part of the
// OfflineIndicator. It's a separate component — OfflineShield —
// that each page renders INLINE in its main content area when
// offline. This is the same pattern as the green/red alert
// shields: each page decides what to show in the center of its
// main content, and when offline it shows <OfflineShield />
// instead of the alert state.
//
// See the page files (GuardDashboard.tsx, etc.) for the
// `{!isOnline ? <OfflineShield /> : <existing alert state>}`
// wrapper.
// =====================================================================

import { Shield, WifiOff } from 'lucide-react';
import { useConnectivity } from '../hooks/useConnectivity';
import { getNoConnectionDescText } from '../lib/offlineStrings';

export default function OfflineIndicator() {
  const { isOnline } = useConnectivity();

  // When online, render nothing.
  if (isOnline) return null;

  // Use the browser language. Falls back to English.
  const language =
    typeof navigator !== 'undefined' ? navigator.language : 'en';
  const description = getNoConnectionDescText(language);

  return (
    // Just the banner. Fixed at the top of the page, right
    // below the Header (top-14 = 56px Header height). z-30 puts
    // it below the Header (z-40) but above the page content.
    // No backdrop, no fullscreen overlay, no overlap.
    <div
      className="fixed top-14 left-0 right-0 z-30 bg-red-600 text-white border-b-2 border-red-400 px-4 py-3 shadow-md flex items-center gap-3"
      role="alert"
      aria-live="assertive"
      data-testid="offline-banner"
    >
      <Shield className="w-5 h-5 flex-shrink-0" />
      <WifiOff className="w-5 h-5 flex-shrink-0" />
      <div className="text-sm font-medium leading-snug">
        {description}
      </div>
    </div>
  );
}
